import {useEffect} from 'react';
import {DOCUMENT_ACTIONS, TASK_ACTIONS} from 'utils/constants';
import {connect} from 'react-redux';
import {loadTasks} from '../mytasks/myTasks.action';
import {actionFinished} from '../mytasks/action/taskAction.action';
import {preventDocumentReload} from '../documentoverview/documentOverview.action';
import {getDocumentOverviewTasks, getActionParameters, getUsersLoggedInData} from '../store/application.reducers';
import {showNotification} from '../notifications/notification.action';
import * as logger from 'utils/logger.function';
import {navigateBetweenDocuments} from '../mytasks/action/navigateBetweenTasks.action';
import {useNavigate} from "react-router-dom";
import * as reduxSelectors from "components/store/application.reducers.js";
import {useModalContext} from "components/modals/ModalContext";


/**
 * asynchronous part of the task action - showing the dialog, which will execute the action
 * synchronous part of action is inside redux action
 */
export const AsyncTaskActionExecutorDocument = (props) => {
    const navigate = useNavigate();
    const modalContext = useModalContext();
    const {toggleModal} = modalContext || {};

    const handleCallback = () => {
        props.preventDocumentReload(false);
        props.reloadTasks();
    }

    const actionParameters = props.actionParameters;

    /**
     * opening the modal is a state update on the host, so it cannot happen while rendering.
     * actionParameters is the redux action object, a stable reference until another action is
     * dispatched - so this runs once per requested action rather than on every re-render.
     */
    useEffect(() => {
        // nothing to show
        if (!actionParameters.type) {
            return;
        }

        let taskList = props.tasks ? props.tasks.rows.map((task) => task) : [];

        const tasksForAction = taskList
            .filter((task) => { // find those matching props.taskIds
                return actionParameters.taskIds.some((taskKeyWanted) => {
                    return taskKeyWanted === task.key;
                });
            });

        /**
         * sometimes, when user has dialog opened and navigates away for some time or changes context
         * the keys for task/history details expire, which then results in empty tasks for the popup
         * so detect this situation and rather hide the popup than check for invalid props inside each popup
         */
        if (tasksForAction.length === 0) {
            logger.error("AsyncTaskActionExecutor", "no task to be shown for these keys: " + actionParameters.taskIds);
            props.actionFinished();
            return;
        }

        const popupProps = {
            closeCallback: props.actionFinished,
            handledTaskCallback: handleCallback,
            translate: props.translate,
            tasks: tasksForAction,
            showNotification: props.showNotification,
            refreshTaskList: props.loadTasks,
            taskDetails: props.taskDetails,
            skipComments: true,
            sendDocument: true,
            navigate: navigate,
            companyId: props.currentContextId
        };

        // according to type either show the modal or execute the action directly ..
        switch (actionParameters.type) {
            case DOCUMENT_ACTIONS.EMAIL:
                toggleModal?.(TASK_ACTIONS.EMAIL, {...popupProps, loggedInData: props.loggedInData});
                break;
            case DOCUMENT_ACTIONS.APPROVE_DIRECTLY: {
                const title = tasksForAction.length === 1
                    ? props.translate?.("popUp.approveDirectly.titleSingle", tasksForAction[0].description)
                    : props.translate?.("popUp.approveDirectly.titleMultiple", tasksForAction.length);
                toggleModal?.(DOCUMENT_ACTIONS.APPROVE_DIRECTLY, {...popupProps, title});
                break;
            }
            case DOCUMENT_ACTIONS.REJECT_DIRECTLY: {
                const title = tasksForAction.length === 1
                    ? props.translate?.("popUp.rejectDirectly.titleSingle", tasksForAction[0].description)
                    : props.translate?.("popUp.rejectDirectly.titleMultiple", tasksForAction.length);
                toggleModal?.(DOCUMENT_ACTIONS.REJECT_DIRECTLY, {...popupProps, title});
                break;
            }
            case DOCUMENT_ACTIONS.SEND_FOR_APPROVAL: {
                const title = tasksForAction.length === 1
                    ? props.translate?.("popUp.sendforapproval.titleSingle", tasksForAction[0].description)
                    : props.translate?.("popUp.sendforapproval.titleMultiple", tasksForAction.length);
                toggleModal?.(DOCUMENT_ACTIONS.SEND_FOR_APPROVAL, {...popupProps, title});
                break;
            }
            case TASK_ACTIONS.NEXT_TASK:
                const nextTaskId = props.navigateBetweenDocuments(tasksForAction[0].key, true);
                navigate(nextTaskId, {replace: true});
                props.actionFinished();
                break;
            case TASK_ACTIONS.PREVIOUS_TASK:
                const previousTaskId = props.navigateBetweenDocuments(tasksForAction[0].key, false);
                navigate(previousTaskId, {replace: true});
                props.actionFinished();
                break;
            case DOCUMENT_ACTIONS.CANCEL_DIRECTLY: {
                const title = props.translate?.("popUp.cancelDocuments.title", tasksForAction[0].description);
                toggleModal?.(DOCUMENT_ACTIONS.CANCEL_DIRECTLY, {...popupProps, title});
                break;
            }
            default:
                console.error("unmapped action type: " + actionParameters.type);
        }

    }, [actionParameters]); // eslint-disable-line react-hooks/exhaustive-deps -- once per action, not per render

    return null;
}


const mapStateToProps = function (store) {
    return {
        tasks: getDocumentOverviewTasks(store),
        actionParameters: getActionParameters(store),
        loggedInData: getUsersLoggedInData(store),
        currentContextId: reduxSelectors.getUsersCurrentContextId(store)
    };
};

const connected = connect(mapStateToProps, {
    actionFinished,
    showNotification,
    loadTasks,
    navigateBetweenDocuments,
    preventDocumentReload
})(AsyncTaskActionExecutorDocument);


export default connected;